import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Users, TrendingUp, Globe, Calendar, MessageSquare } from 'lucide-react';
import { dashboardApi } from '../services/api';
import ConversationThread from './ConversationThread';

const InfluencerProfile = () => {
  const { id } = useParams();
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadInfluencer();
  }, [id]);

  const loadInfluencer = async () => {
    try {
      const response = await dashboardApi.getInfluencer(id);
      setInfluencer(response.data);
      setNewStatus(response.data.status);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load influencer:', error);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (newStatus === influencer.status) return;
    
    try {
      await dashboardApi.updateInfluencerStatus(id, {
        status: newStatus,
        note: `Status changed to ${newStatus}`,
        user: 'dashboard'
      });
      loadInfluencer();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await dashboardApi.addNote(id, {
        title: 'Note',
        note: newNote,
        user: 'dashboard'
      });
      setNewNote('');
      loadInfluencer();
    } catch (error) {
      console.error('Failed to add note:', error);
      alert('Failed to add note');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!influencer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Influencer not found</p>
        <Link to="/influencers" className="text-blue-600 hover:underline mt-4 inline-block">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <Link
        to="/influencers"
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Influencers
      </Link>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {influencer.username.charAt(0).toUpperCase()}
            </div>
            <div className="ml-6">
              <h2 className="text-3xl font-bold text-gray-900">@{influencer.username}</h2>
              {influencer.full_name && (
                <p className="text-lg text-gray-600 mt-1">{influencer.full_name}</p>
              )}
              <div className="flex items-center mt-2 space-x-4">
                {influencer.location && (
                  <span className="flex items-center text-gray-500">
                    <MapPin className="w-4 h-4 mr-1" />
                    {influencer.location}
                  </span>
                )}
                {influencer.is_verified && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    ✓ Verified
                  </span>
                )}
                {influencer.is_business_account && (
                  <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                    Business
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="replied">Replied</option>
              <option value="negotiating">Negotiating</option>
              <option value="partnered">Partnered</option>
              <option value="rejected">Rejected</option>
            </select>
            {newStatus !== influencer.status && (
              <button
                onClick={handleStatusUpdate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update
              </button>
            )}
          </div>
        </div>

        {influencer.biography && (
          <p className="mt-4 text-gray-700">{influencer.biography}</p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Followers</p>
              <p className="text-2xl font-bold mt-1">{influencer.followers_count?.toLocaleString()}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Engagement Rate</p>
              <p className="text-2xl font-bold mt-1">{influencer.engagement_rate}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Posts</p>
              <p className="text-2xl font-bold mt-1">{influencer.posts_count?.toLocaleString()}</p>
            </div>
            <Globe className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Score</p>
              <p className="text-2xl font-bold mt-1">{influencer.qualification_score}/100</p>
              <p className="text-sm text-gray-500 mt-1">{influencer.grade}</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              influencer.qualification_score >= 80 ? 'bg-green-100 text-green-600' :
              influencer.qualification_score >= 70 ? 'bg-blue-100 text-blue-600' :
              'bg-yellow-100 text-yellow-600'
            }`}>
              <span className="text-xl font-bold">{influencer.grade?.[0]}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-3">
              {influencer.email && (
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{influencer.email}</p>
                    <p className="text-xs text-gray-400">Source: {influencer.email_source}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Outreach History */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Outreach History</h3>
            {influencer.outreach && influencer.outreach.length > 0 ? (
              <div className="space-y-4">
                {influencer.outreach.map((item) => (
                  <div key={item.id} className="border-l-4 border-blue-500 pl-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{item.subject}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.replied ? 'bg-green-100 text-green-800' :
                        item.delivered ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.replied ? 'Replied' : item.delivered ? 'Delivered' : 'Sent'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(item.sent_at).toLocaleString()}
                    </p>
                    {item.reply_message && (
                      <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-2 rounded">
                        {item.reply_message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No outreach history</p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Add Note */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Add Note</h3>
            <form onSubmit={handleAddNote}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Note
              </button>
            </form>
          </div>

          {/* Activity Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
            {influencer.activities && influencer.activities.length > 0 ? (
              <div className="space-y-3">
                {influencer.activities.map((activity) => (
                  <div key={activity.id} className="flex items-start">
                    <MessageSquare className="w-4 h-4 text-gray-400 mr-2 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.title}</p>
                      {activity.description && (
                        <p className="text-xs text-gray-600 mt-1">{activity.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 text-sm">No activity yet</p>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <ConversationThread influencerId={id} />
      </div>
    </div>
  );
};

export default InfluencerProfile;
